import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signInAnonymously } from 'firebase/auth';
import { auth } from '../firebase';
import Logger from '../utils/logger';

function Auth({ onClose }) {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      Logger.info(`Attempting ${isLogin ? 'login' : 'signup'} for user: ${email}`);
      
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
        Logger.debug('User logged in successfully');
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
        Logger.debug('User signed up successfully');
      }
      onClose();
    } catch (error) {
      Logger.error('Authentication error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueAsGuest = async () => {
    try {
      await signInAnonymously(auth);
      Logger.debug('Continued as guest');
      onClose();
    } catch (error) {
      Logger.error('Error signing in as guest:', error);
      setError(error.message);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" component="h1" gutterBottom align="center">
        {isLogin ? 'Welcome Back' : 'Create Account'}
      </Typography>

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          required
          autoComplete="email"
        />

        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          required
          autoComplete={isLogin ? 'current-password' : 'new-password'}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          fullWidth
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{
            mt: 3,
            mb: 2,
            bgcolor: 'black',
            '&:hover': { bgcolor: '#333' },
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            isLogin ? 'Sign In' : 'Sign Up'
          )}
        </Button>

        <Button
          fullWidth
          variant="text"
          onClick={() => {
            setIsLogin(!isLogin);
            setError('');
            Logger.debug(`Switched to ${!isLogin ? 'login' : 'signup'} mode`);
          }}
          sx={{ color: 'black', '&:hover': { color: '#333' } }}
        >
          {isLogin ? "Don't have an account? Sign Up" : 'Already have an account? Sign In'}
        </Button>

        <Divider sx={{ my: 2 }}>or</Divider>

        <Button
          fullWidth
          variant="outlined"
          onClick={handleContinueAsGuest}
          sx={{ 
            color: 'black', 
            borderColor: 'black',
            '&:hover': { borderColor: '#333', color: '#333' }
          }}
        >
          Continue as Guest
        </Button>
      </form>
    </Box>
  );
}

export default Auth; 